import accounting from "accounting";
import { _ } from "extensions/underscore";
import upperFirst from "lodash-es/upperFirst";

function isNaNOrUndefined(v) {
    return v === void 0 || Number.isNaN(v);
}

export function getGetter(model) {
    if (model.get !== void 0) {
        return function getFromBackboneModel(prop) { return model.get(prop); };
    }
    
    if (_.isObject(model)) {
        return function getFromPlainObject(prop) { return model[prop]; };
    }
    
    throw new Error("Unknown data structure");
}

/**
 * Calculate rate
 * @param {Object} options
 * @param {Number} options.value
 * @param {Number} options.total
 * @param {Number} options.precision
 * @param {String} options.suffix
 * @returns {*} String if suffix is specified or Float if it is not
 */
export function getRate(options) {
    const opts = {
        precision: 0,
        humanize: false,
        addFloatValue: true,
        rate: false,
        ...options
    };

    const isTotalOrValueZero = opts.rate === 0 || opts.total === 0 || opts.value === 0;
    let rate;
    let rawRate;

    function humanize() {
        const minAllowedValueByPrecision = 10 ** -opts.precision;
        if (rate < minAllowedValueByPrecision && !isTotalOrValueZero && rate > 0) {
            opts.prefix = "< ";
            rate = minAllowedValueByPrecision;
        } else {
            // Make sure that requested rate has correct number of trailing zeros
            // run toFixed again
            rate = accounting.toFixed(rate, opts.precision);
        }
        opts.suffix = "%";
    }

    function checkZeroRate() {
        try {
            rate = Number.parseFloat(rawRate.toFixed(1 - Math.floor(Math.log(rawRate) / Math.log(10))));
        } catch (error) {
            // Implementing conditional catch clause conforming to the ECMAScript specification
            if (error instanceof RangeError && rawRate < 1e-5) {
                rate = 0;
            } else {
                throw error;
            }
        }
    }

    if (isTotalOrValueZero) {
        rate = 0;
        rawRate = 0;
    } else {
        rawRate = opts.rate || (opts.value / opts.total) * 100;

        rate = Number.parseFloat(accounting.toFixed(rawRate, opts.precision));

        // If rate is zero, but we know that neither value nor total is zero
        // we need to get the first 2 non zero digits from a decimal number
        if (rate === 0) {
            checkZeroRate();
        }
    }

    if (opts.humanize === true) {
        humanize();
    }

    if (opts.suffix) {
        rate = rate + opts.suffix;
    }

    if (opts.prefix) {
        rate = opts.prefix + rate;
    }

    // Build return value
    if (opts.humanize && opts.addFloatValue) {
        return {
            string: rate,
            toString: () => rate,
            "float": rawRate
        };
    }

    return rate;
}

/**
 * Get value using getter and data
 * Our data can look like
 * {
     *     approvals_count: 100,
     *     approval_amount: 200
     * }
 * or
 * {
     *     approvals: {
     *         count: 100,
     *         amount: 200
     *     }
     * }
 * This function should first attempt to read underscored value and if it doesn't exist then read from the object
 * @param {Object | Backbone.Model} Data model. Either backbone model or plain object
 * @param {Array | String} walk what to get from the data structure, @example ["refunds", "by"]
 * @param defaultValue If a default value is provided and the property doesn't exist or is undefined then the default will be returned.
 */
export function getValue(model, walk, defaultValue) {
    const getter = getGetter(model);
    let walkString;

    // Normalize walk to array if it's a string
    if (typeof walk === "string") {
        walkString = walk;
        walk = walk.split(walk.includes("_") ? "_" : ".");
    } else {
        walkString = walk.join("_");
    }

    const underscoredValue = getter(walkString);
    let value;

    if (underscoredValue !== void 0) {
        value = underscoredValue;
    } else {
        const walkChain = _.clone(walk);
        value = _.propValue(getter(walkChain.shift()), walkChain);
    }

    return value === void 0 ? defaultValue : value;
}

/**
 * Refunds
 */

export const refunds = {};

refunds.getTotalValue = function(model, by, normalized = true) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + refunds.getTotalValue(current, by, normalized), 0);
    }
    const baseField = normalized === false ? "original_refunds" : "refunds";
    const refundsValue = getValue(model, [baseField, by], 0);

    if (refundsValue !== void 0) {
        return refundsValue || 0;
    }
    throw new Error("Unable to calculate total refunds value");
};

// eslint-disable-next-line unicorn/no-object-as-default-parameter
refunds.getRefundRate = function(model, by, options = { by }) {
    if (_.isObject(by)) {
        options = by;
    }
    if (!_.isObject(options)) {
        options = { by };
    }

    const opts = {
        precision: 2,
        by: "amount",
        ...options
    };
    const refunds_value = Math.abs(refunds.getTotalValue(model, opts.by));
    const sales_value = sales.getTotalValue(model, opts.by);

    if (sales_value <= 0 && refunds_value) {
        return Number.NaN;
    }

    return getRate(_.extend({
        value: refunds_value,
        total: sales_value
    }, opts));
};

refunds.averageTicket = function(model, options) {
    const opts = {
        precision: 2,
        ...options
    };
    const count = refunds.getTotalValue(model, "count", true);
    const amount = Math.abs(refunds.getTotalValue(model, "amount", opts.normalized));
    
    if (count === 0) {
        return 0;
    }
    return Number.parseFloat(accounting.toFixed(amount / count, opts.precision));
};

/**
 * Authorizations
 */
export const authorizations = {};

const defaultAuthOpts = {
    by: "count",
    normalized: true
};

authorizations.declinesValue = function(model, opts) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + authorizations.declinesValue(current, opts), 0);
    }

    const options = {
        declineType: null,
        ...defaultAuthOpts,
        ...opts
    };
    
    const getter = getGetter(model);
    
    const basePrefix = options.normalized === false ? "original_" : "";
    
    const walkArray = [options.declineType ? `${basePrefix}${options.declineType}_declines` : `${basePrefix}declines`, options.by];

    // Check if the model contains direct value for the property, e.g. `declines_amount`
    const declines_direct = getValue(model, walkArray);
    if (Number.isFinite(declines_direct)) {
        return declines_direct;
    }

    // Check if the model contains a getter key
    const declineTypeGetterPrefix = options.declineType ? `${options.declineType}_` : "";
    const getter_direct = getValue(model, `${basePrefix}${declineTypeGetterPrefix}declines${upperFirst(opts.by)}`);
    if (Number.isFinite(getter_direct)) {
        return getter_direct;
    }

    return ["hard", "soft", "reject", "unknown"].reduce((memo, curr) => memo + (getter([curr, ...walkArray].join("_")) || 0), 0);
};

authorizations.approvalsValue = function(model, opts = defaultAuthOpts) {
    if (typeof model === "undefined") {
        throw new TypeError("Model is undefined");
    }
    
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + authorizations.approvalsValue(current, opts), 0);
    }
    const baseField = opts.normalized === false ? "original_approvals" : "approvals";
    
    // Check if the model contains a getter key
    const getter_direct = getValue(model, `${baseField}${upperFirst(opts.by)}`);
    if (Number.isFinite(getter_direct)) {
        return getter_direct;
    }
    
    return getValue(model, [baseField, opts.by]);
};

/**
 * Get total number of authorizations (declines + approvals)
 * @param {Object} model data model
 * @param {Object} [opts] options
 * @returns {*}
 */
authorizations.authorizationsValue = function(model, opts) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + authorizations.authorizationsValue(current, opts), 0);
    }
    
    const getter = getGetter(model);
    const baseField = opts.normalized === false ? "original_auths" : "auths";
    const auth_direct = getter(`${baseField}_${opts.by}`);
    if (Number.isFinite(auth_direct)) {
        return auth_direct;
    }

    // Check if the model contains a getter key
    const baseField2 = opts.normalized === false ? "original_auth" : "auth";
    const getter_direct = getValue(model, `${baseField2}${upperFirst(opts.by)}`);
    if (Number.isFinite(getter_direct)) {
        return getter_direct;
    }

    return (authorizations.approvalsValue(model, opts) || 0) + (authorizations.declinesValue(model, opts) || 0);
};

/**
 * Get average decline value
 * @param {Object} model data model
 * @param {Object} [opts] options
 * @returns {*}
 */
authorizations.averageDeclineTicket = function(model, opts) {
    const count = authorizations.declinesCount(model, opts);
    const amount = authorizations.declinesAmount(model, opts);
    
    if (count === 0) {
        return 0;
    }
    
    const options = {
        precision: 2,
        ...opts
    };
    return Number.parseFloat(accounting.toFixed(amount / count, options.precision));
};

/**
 * Get average decline value
 * @param {Object} model data model
 * @param {Object} [opts] options
 * @returns {*}
 */
authorizations.averageApprovalTicket = function(model, opts) {
    const count = authorizations.approvalsCount(model, opts);
    
    if (count === 0) {
        return 0;
    }
    
    const amount = authorizations.approvalsAmount(model, opts);
    const options = {
        precision: 2,
        ...opts
    };
    return Number.parseFloat(accounting.toFixed(amount / count, options.precision));
};

/**
 * Get average authorization value
 * @param {Object} model data model
 * @param {Object} [opts] options
 * @returns {*}
 */
authorizations.averageAuthTicket = function(model, opts) {
    const count = authorizations.authCount(model, opts);
    if (count === 0) {
        return 0;
    }
    
    const options = {
        precision: 2,
        ...opts
    };
    const amount = authorizations.authAmount(model, opts);
    return Number.parseFloat(accounting.toFixed(amount / count, options.precision));
};

/**
 * Get total count of approvals
 * @param {Object} model data model
 * @param {Object} [opts] options
 */
authorizations.approvalsCount = function(model, opts) {
    return authorizations.approvalsValue(model, {
        ...opts,
        by: "count"
    });
};

/**
 * Get total amount of approvals
 * @param {Object} model data model
 * @param {Object} [opts] options
 */
authorizations.approvalsAmount = function(model, opts) {
    return authorizations.approvalsValue(model, {
        ...opts,
        by: "amount"
    });
};

/**
 * Get total count of declines
 * @param {Object} model data model
 * @param {Object} [opts] options
 */
authorizations.declinesCount = function(model, opts) {
    return authorizations.declinesValue(model, {
        ...opts,
        by: "count"
    });
};

/**
 * Get total amount of declines
 * @param {Object} model data model
 * @param {Object} [opts] options
 */
authorizations.declinesAmount = function(model, opts) {
    return authorizations.declinesValue(model, {
        ...opts,
        by: "amount"
    });
};
/**
 * Get total count of all authorizations
 * @param {Object} model data model
 * @param {Object} [opts] options
 */
authorizations.authCount = function(model, opts) {
    return authorizations.authorizationsValue(model, {
        ...opts,
        by: "count"
    });
};

/**
 * Get total amount of all authorizations
 * @param {Object} model data model
 * @param {Object} [opts] options
 */
authorizations.authAmount = function(model, opts) {
    return authorizations.authorizationsValue(model, {
        ...opts,
        by: "amount"
    });
};

/**
 * Get approval rate from the object
 * @param {Object} model data model
 * @param {Object} opts options
 */
authorizations.approvalRate = function(model, opts) {
    const options = {
        by: "count",
        precision: 2,
        ...opts
    };

    // API v2 passes approval rate is in the object
    const approval_rate = getValue(model, `approval_rate_${options.by}`);
    if (Number.isFinite(approval_rate)) {
        return getRate({
            rate: approval_rate,
            ...options
        });
    }

    // If it's not there let's compute one
    const approvalsValue = authorizations.approvalsValue(...arguments);
    const authorizationsValue = authorizations.authorizationsValue(...arguments);

    if (isNaNOrUndefined(approvalsValue) || isNaNOrUndefined(authorizationsValue)) {
        return getRate({
            value: 0,
            total: 0,
            ...options
        });
    }
    return getRate({
        value: approvalsValue,
        total: authorizationsValue,
        ...options
    });
};

authorizations.declineRate = function(model, opts) {
    const options = {
        by: "count",
        precision: 2,
        ...opts
    };

    const declinesValue = authorizations.declinesValue(...arguments);
    const authorizationsValue = authorizations.authorizationsValue(...arguments);

    if (isNaNOrUndefined(declinesValue) || isNaNOrUndefined(authorizationsValue)) {
        return getRate({
            value: 0,
            total: 0,
            ...options
        });
    }
    return getRate({
        value: declinesValue,
        total: authorizationsValue,
        ...options
    });
};

/**
 * Fees
 */
export const fees = {};

fees.getTotalFeesAmount = function(model, normalized = true) {
    if (_.isArray(model)) {
        return model.reduce((acc, current) => acc + fees.getTotalFeesAmount(current, normalized), 0);
    }
    const getter = getGetter(model);
    let field;
    
    if (normalized) {
        field = "fees_amount";
    } else {
        field = "original_fees_amount";
    }
    
    const fees_amount_direct = getter(field);

    if (Number.isFinite(fees_amount_direct)) {
        return fees_amount_direct;
    }
    
    return Number.NaN;
};

/**
 * Calculate Gross Effective Rate from cash-flow
 * Gross Effective rate = Total Fees / Sales
 * https://pazien.atlassian.net/wiki/display/PAZ/Fees#Fees-Calculationstoremember
 *
 * @param model
 * @param {Object} [options]
 * @param {Array} [options.feesTypes] optional array to pass only required fee type
 */
fees.grossEffectiveRate = function(model, options) {
    const opts = {
        precision: 2,
        suffix: "",
        ...options
    };

    const getter = getGetter(model);

    // Return earlier if found effective_rate in the object itself
    const direct_effective_rate = getter("effective_fee_rate");
    if (direct_effective_rate) {
        return getRate({
            ...opts,
            rate: direct_effective_rate
        });
    }
    
    const fees_amount = fees.getTotalFeesAmount(model);
    const sales_amount = sales.getTotalValue(model, "amount") || 0;
    
    if (sales_amount <= 0 && fees_amount) {
        return Number.NaN;
    }

    return getRate({
        ...opts,
        value: fees_amount,
        total: sales_amount
    });
};

fees.netEffectiveRate = function(model, options) {
    const opts = {
        precision: 2,
        suffix: "",
        ...options
    };
    
    const getter = getGetter(model);
    
    // Return earlier if found net_effective_rate in the object itself
    const direct_net_effective_rate = getter("net_effective_fee_rate");
    if (direct_net_effective_rate) {
        return getRate({
            ...opts,
            rate: direct_net_effective_rate
        });
    }
    
    const fees_amount = fees.getTotalFeesAmount(model);
    const sales_amount = sales.getTotalValue(model, "amount") || 0;
    const refunds_amount = refunds.getTotalValue(model, "amount") || 0;
    
    if (sales_amount - refunds_amount <= 0 && fees_amount) {
        return Number.NaN;
    }
    
    return getRate({
        ...opts,
        value: fees_amount,
        total: sales_amount - refunds_amount
    });
};

/**
 * Chargebacks
 */

export const chargebacks = {};

chargebacks.getTotalValue = function(model, by = "count", normalized = true) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + chargebacks.getTotalValue(current, by, normalized), 0);
    }
    
    const getter = getGetter(model);
    const chargebacksVal = getter("chargebacks");

    const baseField = normalized === false ? "original_chargebacks" : "chargebacks";
    const chargebacks_direct = getter(`${baseField}_${by}`);
    if (Number.isFinite(chargebacks_direct)) {
        return chargebacks_direct;
    }
    
    const chargebacks_direct_getter = getter(`${baseField}${upperFirst(by)}`);
    if (Number.isFinite(chargebacks_direct_getter)) {
        return chargebacks_direct_getter;
    }
    
    let memoIncreaser;
    if (chargebacksVal !== void 0) {
        // Dealing with standard response model
        memoIncreaser = (cat) => chargebacksVal[`${cat}_${by}`] || 0;
    } else {
        // Dealing with internal aggregated model
        memoIncreaser = function(cat) {
            const value = getter(`${cat}_${by}`);

            // If value is not defined, equal to null, or equal to NaN then return NaN
            // eslint-disable-next-line eqeqeq
            if (value == null || Number.isNaN(value)) {
                return Number.NaN;
            }
            return value;
        };
    }

    return ["fraud", "quality", "technical", "clerical", "other"].reduce((memo, chargebackCategory) => {
        const step = memoIncreaser(chargebackCategory);
        if (Number.isNaN(step)) {
            return memo;
        }
        return memo + step;
    }, 0);
};

chargebacks.chargebackRate = function(model, options) {
    const opts = {
        precision: 2,
        ...options
    };

    // API v2 passes chargeback rate in the object if there was pa
    const chargebackRate = getValue(model, "chargebacks_rate");
    if (Number.isFinite(chargebackRate)) {
        return getRate({
            ...opts,
            rate: chargebackRate
        });
    }
    
    // Check if the model contains a getter key
    const getter_direct = getValue(model, "chargebacks_rate");
    if (Number.isFinite(getter_direct)) {
        return getRate({
            ...opts,
            rate: getter_direct
        });
    }
    
    return getRate({
        ...opts,
        value: chargebacks.getTotalValue(model, "count"),
        total: sales.getTotalValue(model, "count")
    });
};

chargebacks.averageTicket = function(model, options) {
    const opts = {
        precision: 2,
        ...options
    };
    
    const count = chargebacks.getTotalValue(model, "count", true);
    const amount = chargebacks.getTotalValue(model, "amount", opts.normalized);
    if (count === 0) {
        return 0;
    }
    
    return Number.parseFloat(accounting.toFixed(amount / count, opts.precision));
};

/**
 * Sales
 */

export const sales = {};

sales.cashInAmount = function(model, normalized = true) {
    return sales.getTotalValue(model, "amount", normalized);
};

sales.cashOutAmount = function(model, normalized = true) {
    const getter = getGetter(model);
    
    const chargebacks_amount = chargebacks.getTotalValue(model, "amount", normalized);
    const refunds_amount = refunds.getTotalValue(model, "amount", normalized);
    const fees_and_taxes_amount = (normalized ? getter("fees_and_taxes_amount") : getter("original_fees_and_taxes_amount")) || 0;
    
    return refunds_amount + fees_and_taxes_amount + chargebacks_amount;
};

sales.netAmount = function(model, normalized = true) {
    return sales.cashInAmount(model, normalized) - sales.cashOutAmount(model, normalized);
};

sales.getTotalValue = function(model, by = "count", normalized = true) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + sales.getTotalValue(current, by, normalized), 0);
    }
    const baseField = normalized === false ? "original_sales" : "sales";
    const salesValue = getValue(model, [baseField, by], 0);

    if (salesValue !== void 0) {
        // Dealing with standard cash flow response model
        return salesValue || 0;
    }
    throw new Error("Cannot calculate sales value");
};

sales.getTotalNetSalesAmount = function(model, normalized = true) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + sales.getTotalNetSalesAmount(current, normalized), 0);
    }
    const baseField = normalized === false ? "original_net_sales_amount" : "net_sales_amount";
    const salesValue = getValue(model, [baseField], 0);
    
    if (salesValue !== void 0) {
        return salesValue || 0;
    }
    
    throw new Error("Cannot calculate sales value");
};

sales.getTotalSalesPurchaseAmount = function(model) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + sales.getTotalSalesPurchaseAmount(current), 0);
    }
    const salesValue = getValue(model, ["sales_purchase_amount"], 0);
    
    if (salesValue !== void 0) {
        return salesValue || 0;
    }
    
    throw new Error("Cannot calculate sales value");
};

sales.averageTicket = function(model, normalized = true) {
    const count = sales.getTotalValue(model, "count");
    const amount = sales.getTotalValue(model, "amount", normalized);

    if (count === 0) {
        return 0;
    }
    return Number.parseFloat(accounting.toFixed(amount / count, 2));
};

sales.getTotalCashBackValue = function(model, by = "count", normalized = true) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + sales.getTotalCashBackValue(current, by, normalized), 0);
    }
    const baseField = normalized === false ? "original_cashback" : "cashback";
    const salesValue = getValue(model, [baseField, by], 0);
    
    if (salesValue !== void 0) {
        // Dealing with standard cash flow response model
        return salesValue || 0;
    }
    throw new Error("Cannot calculate sales value");
};

sales.getAverageCashBack = function(model, options) {
    const opts = {
        precision: 2
    };
    const count = sales.getTotalCashBackValue(model, "count", true);
    
    const amount = sales.getTotalCashBackValue(model, "amount", opts.normalized);
    if (count === 0) {
        return 0;
    }
   
    return Number.parseFloat(accounting.toFixed(amount / count, opts.precision));
};

sales.getTotalOrderValue = function(model, by = "count", normalized = true) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + sales.getTotalOrderValue(current, by, normalized), 0);
    }
    const baseField = normalized === false ? "original_order" : "order";
    const orderValue = getValue(model, [baseField, by], 0);
    if (orderValue !== void 0) {
        // Dealing with standard cash flow response model
        return orderValue || 0;
    }
    throw new Error("Cannot calculate sales value");
};

sales.getAverageOrder = function(model, options) {
    const opts = {
        precision: 2
    };
    const count = sales.getTotalValue(model, "count", true);
    const amount = sales.getTotalOrderValue(model, "amount", opts.normalized);
    if (count === 0) {
        return 0;
    }
    return Number.parseFloat(accounting.toFixed(amount / count, opts.precision));
};

/**
 * Payouts
 */
export const payouts = {};

payouts.getTotalValue = function(model, by = "count", normalized = true) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + payouts.getTotalValue(current, by, normalized), 0);
    }
    const baseField = "oct";
    const payoutsValue = getValue(model, [baseField, by], 0);
    
    if (payoutsValue !== void 0) {
        // Dealing with standard cash flow response model
        return payoutsValue || 0;
    }
    throw new Error("Cannot calculate payouts value");
};

payouts.averagePayout = function(model, normalized = true) {
    const count = payouts.getTotalValue(model, "count");
    const amount = payouts.getTotalValue(model, "amount", normalized);
    
    if (count === 0) {
        return 0;
    }
    return Number.parseFloat(accounting.toFixed(amount / count, 2));
};

// eslint-disable-next-line unicorn/no-object-as-default-parameter
payouts.getPayoutRate = function(model, by, options = { by }) {
    if (_.isObject(by)) {
        options = by;
    }
    if (!_.isObject(options)) {
        options = { by };
    }
    
    const opts = {
        precision: 2,
        by: "amount",
        ...options
    };
    const payouts_value = Math.abs(payouts.getTotalValue(model, opts.by));
    const sales_value = sales.getTotalValue(model, opts.by);
    
    if (sales_value <= 0 && payouts_value) {
        return Number.NaN;
    }
    
    return getRate({
        value: payouts_value,
        total: sales_value
    }, opts);
};
/**
 * Fraud
 */
export const fraud = {};

fraud.getFraudPurchaseAmount = function(model) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + fraud.getFraudPurchaseAmount(current), 0);
    }
    const fraudValue = getValue(model, ["fraud_purchase_amount"], 0);
    
    if (fraudValue !== void 0) {
        return fraudValue || 0;
    }
    
    throw new Error("Cannot calculate fraud amount value");
};

fraud.getFraudStlAmount = function(model) {
    if (_.isArray(model)) {
        return _.reduce(model, (acc, current) => acc + fraud.getFraudStlAmount(current), 0);
    }
    const fraudValue = getValue(model, ["fraud_stl_amount"], 0);
    
    if (fraudValue !== void 0) {
        return fraudValue || 0;
    }
    
    throw new Error("Cannot calculate fraud amount value");
};

fraud.fraudPurchaseRate = function(model, options) {
    const opts = {
        precision: 2,
        suffix: "",
        ...options
    };
    
    const getter = getGetter(model);
    
    // Return earlier if found effective_rate in the object itself
    const direct_fraud_rate = getter("fraud_rate_purchase");
    if (direct_fraud_rate) {
        return getRate({
            ...opts,
            rate: direct_fraud_rate
        });
    }
    
    const fraud_amount = fraud.getFraudPurchaseAmount(model);
    const sales_amount = sales.getTotalSalesPurchaseAmount(model);
    
    if (sales_amount <= 0 && fraud_amount) {
        return Number.NaN;
    }
    
    return getRate({
        ...opts,
        value: fraud_amount,
        total: sales_amount
    });
};

fraud.fraudStlRate = function(model, options) {
    const opts = {
        precision: 2,
        suffix: "",
        ...options
    };
    const getter = getGetter(model);
    
    // Return earlier if found effective_rate in the object itself
    const direct_fraud_rate = getter("fraud_rate_stl");
    if (direct_fraud_rate) {
        return getRate({
            ...opts,
            rate: direct_fraud_rate
        });
    }
    
    const fraud_amount = fraud.getFraudStlAmount(model);
    const sales_amount = sales.getTotalValue(model, "amount");
    
    if (sales_amount <= 0 && fraud_amount) {
        return Number.NaN;
    }
    
    return getRate({
        ...opts,
        value: fraud_amount,
        total: sales_amount
    });
};
